import { documentToReactComponents, Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS, Document, INLINES } from "@contentful/rich-text-types";
import { Box, SxProps, Typography } from "@mui/material";
import { ReactNode } from "react";
import { routes } from "../../constants/routes";
import { Asset, Scalars } from "../../models/schema";
import { colors } from "../../theme/colors";
import { ExternalLink } from "./ExternalLink";
import { InternalLink } from "./InternalLink";

type Props = {
  content: Scalars["JSON"];
  link?: Asset;
  options?: Options;
  sx?: SxProps;
  prefix?: ReactNode | string;
  suffix?: ReactNode | string;
  renderEmbeddedEntry?: (node: any) => ReactNode;
  children?: ReactNode;
  childrenPosition?: "before" | "after";
};

const RenderContent = ({
  content,
  link,
  options = {},
  sx = {},
  prefix = "",
  suffix = "",
  children,
  childrenPosition = "after",
  renderEmbeddedEntry
}: Props) => {
  const defaultOptions: Options = {
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => (
        <Typography variant="h1" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <Typography variant="h2" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <Typography variant="h3" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <Typography variant="h4" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <Typography variant="h5" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <Typography variant="h6" textAlign="center">
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
          {prefix}
          {children}
          {suffix}
        </Typography>
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <Typography
          component="blockquote"
          sx={{
            p: { xs: 4, md: 7 },
            color: colors.grey3,
            borderLeft: `5px solid ${colors.yellow}`,
            "& > p": {
              fontSize: "1.3rem",
              fontStyle: "italic"
            }
          }}>
          {children}
        </Typography>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <Box
          component="ul"
          sx={{
            pl: "1.2rem",
            "& ul": {
              pl: "2.5rem"
            }
          }}>
          {children}
        </Box>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <Typography
          variant="body1"
          component="li"
          sx={{
            "&::marker": {
              content: '"■ "',
              fontSize: "1.5rem",
              color: colors.yellow
            },
            "& > ul > li::marker": {
              content: '"□ "',
              fontSize: "1.5rem",
              color: colors.yellow
            }
          }}>
          {children}
        </Typography>
      ),
      [INLINES.HYPERLINK]: (node, children) => {
        const data = node.data as any;
        const internal = Object.values(routes)
          .map((v) => v.replace("/", ""))
          .filter((v) => v !== "")
          .find((val) => data.uri.replace("/", "") === val);

        return internal ? (
          <InternalLink sx={{ color: colors.darkYellow }} to={data.uri}>
            {prefix}
            {children}
            {suffix}
          </InternalLink>
        ) : (
          <ExternalLink sx={{ color: colors.darkYellow }} href={data.uri}>
            {prefix}
            {children}
            {suffix}
          </ExternalLink>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (_, children) =>
        link ? (
          <ExternalLink href={link?.url as string}>
            {prefix}
            {children}
            {suffix}
          </ExternalLink>
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {prefix}
            {children}
            {suffix}
          </Typography>
        ),
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        if (renderEmbeddedEntry) {
          return renderEmbeddedEntry(node);
        }
        return null;
      }
    }
  };

  const merged: Options = {
    renderNode: {
      ...defaultOptions.renderNode,
      ...options.renderNode
    },
    renderMark: {
      ...defaultOptions.renderMark,
      ...options.renderMark
    },
    renderText: options.renderText || defaultOptions.renderText
  };

  return (
    <Box sx={sx}>
      {childrenPosition === "before" && children}
      {typeof content === "string"
        ? content
        : documentToReactComponents(content as unknown as Document, merged)}
      {childrenPosition === "after" && children}
    </Box>
  );
};

export default RenderContent;
